import { lazy, Suspense, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import cn from "@/lib/clsx";
import { Link, useLocation } from "react-router-dom";
import {
  User,
  Heart,
  Calendar,
  Compass,
  MessageCircle,
  Settings,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  LogOut,
  Settings as SettingsIcon,
  HelpCircle,
  User as UserIcon,
  ChevronDown,
} from "lucide-react";

// Lazy-loaded sub-panels mapped exactly to your file layout references
const ClientProfile = lazy(() => import("@/pages/client/Dashboard"));
const ClientFavorite = lazy(() => import("@/pages/client/favorite/Favorite"));
const Booking = lazy(() => import("@/pages/client/booking/Booking"));
const Explore = lazy(() => import("@/pages/client/explore/Explore"));
const Messages = lazy(() => import("@/pages/client/messages/Messages"));
const ClientSetting = lazy(() => import("@/pages/client/setting/Setting"));

// IS AUTH
import { useAuth } from "@/hooks/useAuth";
import useLogout from "@/hooks/useLogout";
import GenericLoader from "@/components/ui/GenericLoader";
// import { initials } from "@/helpers/initials";

// Mock notifications data
const mockNotifications = [
  {
    id: 1,
    title: "Booking Confirmed",
    message: "Your booking at Luxury Villa has been confirmed",
    time: "2 minutes ago",
    type: "success",
    read: false,
    icon: "✅",
  },
  {
    id: 2,
    title: "New Message",
    message: "John Mwangi sent you a message about Haven View",
    time: "15 minutes ago",
    type: "info",
    read: false,
    icon: "💬",
  },
  {
    id: 3,
    title: "Payment Successful",
    message: "Your payment of KSh 45,000 has been processed",
    time: "1 hour ago",
    type: "success",
    read: true,
    icon: "💰",
  },
  {
    id: 4,
    title: "Property Expiring",
    message: 'Your favorite property "City Heights" will expire soon',
    time: "3 hours ago",
    type: "warning",
    read: true,
    icon: "⚠️",
  },
  {
    id: 5,
    title: "New Property Available",
    message: "Check out new listings in your preferred area",
    time: "5 hours ago",
    type: "info",
    read: true,
    icon: "🏠",
  },
];

export default function ClientLayout() {
  const [searchParams] = useSearchParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState(mockNotifications);
  const logout = useLogout();

  // Extract active query parameter string, defaulting to "profile"
  const currentTab = searchParams.get("tab") || "profile";
  const location = useLocation();
  const base = "/client";

  // Check against the current query string
  const isActive = (path: string) => {
    if (!location.search) {
      return path === "?tab=profile";
    }
    return location.search === path;
  };

  const items = [
    { label: "Profile", path: "?tab=profile", icon: User },
    { label: "Favorites", path: "?tab=favorites", icon: Heart },
    { label: "Bookings", path: "?tab=bookings", icon: Calendar },
    { label: "Explore", path: "?tab=explore", icon: Compass },
    { label: "Messages", path: "?tab=messages", icon: MessageCircle },
    { label: "Settings", path: "?tab=settings", icon: Settings },
  ];

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  // Handle resize for auto-collapse on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".notifications-dropdown") &&
        !target.closest(".notifications-button")
      ) {
        setShowNotifications(false);
      }
      if (
        !target.closest(".user-menu-dropdown") &&
        !target.closest(".user-menu-button")
      ) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Toggle collapse on desktop
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // IS AUTHENTICATED
  const currentLoc = encodeURIComponent(
    window.location.pathname + window.location.search + window.location.hash,
  );
  const user = useAuth().user;

  // Mark notification as read
  const markAsRead = (id: number) => {
    setNotifications((prev) =>
      prev.map((notif) => (notif.id === id ? { ...notif, read: true } : notif)),
    );
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
  };

  // Get unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Get notification style

  // const getNotificationStyle = (type: string) => {
  //   switch (type) {
  //     case "success":
  //       return "bg-green-50 border-green-200 text-green-800";
  //     case "warning":
  //       return "bg-yellow-50 border-yellow-200 text-yellow-800";
  //     case "info":
  //       return "bg-blue-50 border-blue-200 text-blue-800";
  //     default:
  //       return "bg-gray-50 border-gray-200 text-gray-800";
  //   }
  // };

  return (
    <Suspense
      fallback={
        <GenericLoader
          type={"page"}
          message={"Getting things ready"}
          customLabel={"MY PROFILE"}
        />
      }
    >
      <div className="bg-bg text-text-main font-display flex min-h-screen antialiased">
        {/* Mobile Overlay */}
        {isMobileOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setIsMobileOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={cn(
            "fixed top-0 left-0 z-50 flex h-screen flex-col bg-sky-500 transition-all duration-300 lg:sticky",
            isMobileOpen
              ? "w-64 translate-x-0"
              : "-translate-x-full lg:translate-x-0",
            isCollapsed && !isMobileOpen ? "w-20" : "w-64",
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-sky-400/30 p-4">
            {(!isCollapsed || isMobileOpen) && (
              <span className="text-xl font-bold text-white">Dashboard</span>
            )}
            <div className="flex items-center gap-2">
              {/* Mobile Toggle */}
              <button
                onClick={toggleMobileMenu}
                className="rounded-lg p-2 text-white transition-colors hover:bg-sky-600 lg:hidden"
                aria-label="Toggle menu"
              >
                {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              {/* Desktop Collapse Toggle */}
              <button
                onClick={toggleCollapse}
                className="hidden rounded-lg p-2 text-white transition-colors hover:bg-sky-600 lg:flex"
                aria-label="Toggle collapse"
              >
                {isCollapsed ? (
                  <ChevronRight size={20} />
                ) : (
                  <ChevronLeft size={20} />
                )}
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2 overflow-y-auto p-4">
            {items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={`${base}${item.path}`}
                  className={cn(
                    "group relative flex items-center gap-3 rounded-lg px-3 py-3 transition-all duration-200",
                    active
                      ? "bg-white/20 text-white shadow-lg"
                      : "text-white/80 hover:bg-white/10 hover:text-white",
                  )}
                  onMouseEnter={() => setHoveredItem(item.label)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <Icon size={20} className="shrink-0" />

                  {(!isCollapsed || isMobileOpen) && (
                    <span className="truncate">{item.label}</span>
                  )}

                  {/* Tooltip for collapsed state */}
                  {isCollapsed &&
                    !isMobileOpen &&
                    hoveredItem === item.label && (
                      <div className="absolute left-full z-50 ml-2 rounded-lg bg-sky-900 px-3 py-1.5 text-sm whitespace-nowrap text-white shadow-lg">
                        {item.label}
                        <div className="absolute top-1/2 left-0 -ml-1 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                      </div>
                    )}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="border-t border-sky-400/30 p-4">
            {(!isCollapsed || isMobileOpen) && (
              <div className="text-sm text-white/60">v2.0.0</div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <div className="min-w-0 flex-1">
          {/* Tab Navigation header */}
          <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
            <div className="px-4 py-3 sm:px-6 lg:px-8">
              <nav className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Mobile Menu Button (visible when sidebar is hidden) */}
                  <button
                    onClick={toggleMobileMenu}
                    className="text-gray-700 transition-colors hover:text-sky-500 lg:hidden"
                    aria-label="Open menu"
                  >
                    <Menu size={24} />
                  </button>

                  {/* Search Bar */}
                  <div className="relative hidden md:flex">
                    <Search
                      className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400"
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Search..."
                      className="w-48 rounded-lg border border-gray-200 bg-gray-50 py-2 pr-4 pl-10 text-sm text-gray-700 placeholder-gray-400 transition-all focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-200 focus:outline-none lg:w-64"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Notifications */}
                  <div className="relative">
                    <button
                      onClick={() => setShowNotifications(!showNotifications)}
                      className="notifications-button relative rounded-full p-2 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700"
                    >
                      <Bell size={20} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-md">
                          {unreadCount}
                        </span>
                      )}
                    </button>

                    {/* Notifications Dropdown */}
                    {showNotifications && (
                      <div className="notifications-dropdown animate-in slide-in-from-top-2 absolute right-0 mt-2 w-[320px] rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all sm:w-95">
                        <div className="flex items-center justify-between border-b border-gray-100 p-4">
                          <div>
                            <h3 className="font-semibold text-gray-800">
                              Notifications
                            </h3>
                            <p className="text-xs text-gray-400">
                              {unreadCount} unread
                            </p>
                          </div>
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllAsRead}
                              className="text-xs font-medium text-blue-500 transition-colors hover:text-blue-700"
                            >
                              Mark all read
                            </button>
                          )}
                        </div>

                        <div className="max-h-90 overflow-y-auto">
                          {notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8">
                              <Bell size={32} className="text-gray-300" />
                              <p className="mt-2 text-sm text-gray-500">
                                No notifications
                              </p>
                            </div>
                          ) : (
                            notifications.map((notification) => (
                              <div
                                key={notification.id}
                                className={`flex cursor-pointer items-start gap-3 border-b border-gray-50 p-4 transition-colors hover:bg-gray-50 ${
                                  !notification.read ? "bg-blue-50/30" : ""
                                }`}
                                onClick={() => markAsRead(notification.id)}
                              >
                                <div className="mt-0.5 text-xl">
                                  {notification.icon}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between">
                                    <p className="text-sm font-medium text-gray-800">
                                      {notification.title}
                                    </p>
                                    {!notification.read && (
                                      <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                                    )}
                                  </div>
                                  <p className="line-clamp-2 text-xs text-gray-500">
                                    {notification.message}
                                  </p>
                                  <p className="mt-1 text-[10px] text-gray-400">
                                    {notification.time}
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>

                        <div className="border-t border-gray-100 p-3 text-center">
                          <button className="text-xs font-medium text-blue-500 transition-colors hover:text-blue-700">
                            View all notifications
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* User Profile / Login */}
                  {user ? (
                    <div className="relative">
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="user-menu-button group flex items-center gap-2 rounded-full transition-all hover:bg-gray-100"
                      >
                        <div className="h-9 w-9 overflow-hidden rounded-full border-2 border-gray-200 shadow-sm transition-all group-hover:border-blue-400">
                          <img
                            src={
                              user.avatar ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || "User")}&background=3B82F6&color=fff&size=36&bold=true`
                            }
                            alt={user.name || "Profile"}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || "User")}&background=3B82F6&color=fff&size=36&bold=true`;
                            }}
                          />
                        </div>
                        <span className="hidden text-sm font-medium text-gray-700 lg:inline-block">
                          {user.name?.split(" ")[0] || "User"}
                        </span>
                        <ChevronDown
                          size={16}
                          className="hidden text-gray-400 lg:block"
                        />
                      </button>

                      {/* User Menu Dropdown */}
                      {showUserMenu && (
                        <div className="user-menu-dropdown animate-in slide-in-from-top-2 absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all">
                          <div className="border-b border-gray-100 p-4">
                            <p className="font-medium text-gray-800">
                              {user.name || "User"}
                            </p>
                            <p className="truncate text-xs text-gray-500">
                              {user.email}
                            </p>
                          </div>

                          <div className="p-2">
                            <Link
                              to="/client?tab=profile"
                              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                              onClick={() => setShowUserMenu(false)}
                            >
                              <UserIcon size={16} />
                              My Profile
                            </Link>
                            <Link
                              to="/client?tab=settings"
                              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                              onClick={() => setShowUserMenu(false)}
                            >
                              <SettingsIcon size={16} />
                              Settings
                            </Link>
                            <Link
                              to="/help"
                              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                              onClick={() => setShowUserMenu(false)}
                            >
                              <HelpCircle size={16} />
                              Help Center
                            </Link>
                          </div>

                          <div className="border-t border-gray-100 p-2">
                            <button
                              onClick={() => {
                                // Handle logout
                                logout({ stayLoggedOut: true });
                                setShowUserMenu(false);
                              }}
                              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                            >
                              <LogOut size={16} />
                              Logout
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link to={`/auth/login?returnTo=${currentLoc}`}>
                      <button className="flex items-center gap-2 rounded-full border-2 border-blue-500 bg-white px-5 py-1.5 font-medium text-blue-600 shadow-sm transition-all hover:scale-105 hover:bg-blue-50 active:scale-95">
                        Login
                      </button>
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          </header>

          {/* Main Tab Viewport Frame */}
          <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="p-4 shadow-sm">
              {/* Suspense is strictly mandatory when handling lazy-loaded bundles */}
              <Suspense fallback={<TabSuspenseFallback />}>
                {currentTab === "profile" && <ClientProfile />}
                {currentTab === "favorites" && <ClientFavorite />}
                {currentTab === "bookings" && <Booking />}
                {currentTab === "explore" && <Explore />}
                {currentTab === "messages" && <Messages />}
                {currentTab === "settings" && <ClientSetting />}
              </Suspense>
            </div>
          </main>
        </div>
      </div>
    </Suspense>
  );
}

/**
 * Micro-layout loading indicator that scales seamlessly to
 * match the internal container height of your panels while loading chunks.
 */
function TabSuspenseFallback() {
  return (
    <div className="flex min-h-[40vh] w-full flex-col justify-center gap-4 py-6">
      <div className="bg-brand-secondary/50 h-6 w-1/4 animate-pulse rounded-md" />
      <div className="bg-brand-secondary/30 h-4 w-3/4 animate-pulse rounded-md" />
      <div className="mt-4 space-y-3">
        <div className="bg-brand-secondary/20 h-12 w-full animate-pulse rounded-lg" />
        <div className="bg-brand-secondary/20 h-12 w-full animate-pulse rounded-lg" />
      </div>
    </div>
  );
}
